import { ChipSelect, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
/**
 * The same five metrics, with the platform as the axis instead of a filter.
 *
 * A filter answers "how are we doing on ChatGPT". This answers "which engines
 * are we losing on", which is the question that decides where the next page
 * goes — and it cannot be read by setting the filter eleven times and writing
 * the numbers down.
 *
 * The matrix on top is brands down, platforms across, one metric at a time. One
 * metric, because a table whose cells came from two metrics is a table whose
 * cells mean different things, and nobody reads the column header twice.
 */

import { useQuery } from "@tanstack/react-query";
import { BarChart3, Layers } from "lucide-react";
import { useState } from "react";
import { api, METRICS, type MetricName, type PlatformsTab } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Bars, value } from "~/modules/analytics/components/chart";
import { DrillLink } from "~/modules/analytics/components/detail";
import { InsightIcon } from "~/modules/analytics/components/insight-icon";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { readingPath } from "~/modules/analytics/lib/loop";

export function InsightPlatforms({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const [metric, setMetric] = useState<MetricName>("visibility");

	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"insights",
			"platforms",
			{
				range: filters.range,
				engine: filters.engine,
				locale: filters.locale,
				topics: filters.topics,
				intent: filters.intent,
			},
		],
		queryFn: () => api.insights.platforms(entityId, filters),
		...read,
	});

	return (
		<StageShell entityId={entityId} reading="platforms" filters={filters} setFilters={setFilters}>
			<Async state={state}>
				{(data) =>
					data.platforms.length === 0 ? (
						<EmptyState
							size="compact"
							title={t("insights.empty")}
							description={t("insights.emptyBody")}
							action={<DrillLink to={readingPath(brand(entityId), "prompts")}>{t("empty.goPrompts")}</DrillLink>}
						/>
					) : (
						<div className="insight-dashboard platforms-dashboard">
							<Section
								title={t("platforms.matrix")}
								icon={
									<InsightIcon tone="analyse">
										<Layers />
									</InsightIcon>
								}
								lede={t("platforms.matrixHint")}
								action={
									<ChipSelect
										value={metric}
										onChange={(next) => setMetric(next as MetricName)}
										aria-label={t("platforms.metric")}
										options={METRICS.map((one) => ({ value: one, label: t(`metric.${one}`) }))}
									/>
								}
							>
								<Grid data={data} metric={metric} />
							</Section>

							{data.metrics
								.filter((block) => block.metric === metric)
								.map((block) => (
									<Section
										key={block.metric}
										title={t(`metric.${block.metric}`)}
										icon={
											<InsightIcon tone="detect">
												<BarChart3 />
											</InsightIcon>
										}
										lede={t(`metric.${block.metric}.hint`)}
									>
										<div className="metric-panel">
											<div className="metric-figure">
												<Bars
													metric={block.metric}
													rows={block.rows.map((row) => ({
														key: row.engine,
														label: t(`engine.${row.engine}`),
														value: row.value,
													}))}
												/>
											</div>
											<div className="metric-rank">
												<Table variant="resource" className="rank-table">
													<TableHeader>
														<TableRow>
															<TableHead className="wrap">{t("filter.engine")}</TableHead>
															<TableHead className="num">{t("insights.runs")}</TableHead>
															<TableHead className="num">{t(`metric.${block.metric}`)}</TableHead>
														</TableRow>
													</TableHeader>
													<TableBody>
														{block.rows.map((row) => (
															<PlatformRow key={row.engine} row={row} metric={block.metric} />
														))}
													</TableBody>
												</Table>
											</div>
										</div>
									</Section>
								))}
						</div>
					)
				}
			</Async>
		</StageShell>
	);
}

function PlatformRow({
	row,
	metric,
}: {
	row: { engine: string; samples: number; value: number | null; rank: number | null };
	metric: MetricName;
}) {
	const t = useT();
	const { locale } = useI18n();
	return (
		<TableRow>
			<TableCell className="wrap">
				<span className="rank-place num">{row.rank ?? "—"}</span>
				{t(`engine.${row.engine}`)}
			</TableCell>
			<TableCell className="num mono">{row.samples}</TableCell>
			<TableCell className="num mono">{value(locale, metric, row.value)}</TableCell>
		</TableRow>
	);
}

/**
 * Brands down, platforms across, one metric in the cells.
 *
 * The number is the content and the tint is the second channel, scaled against
 * the largest cell in the table. A tint alone would be unreadable to anyone who
 * cannot separate the shades, and unquotable to everybody else.
 */
function Grid({ data, metric }: { data: PlatformsTab; metric: MetricName }) {
	const t = useT();
	const { locale } = useI18n();
	const block = data.metrics.find((one) => one.metric === metric);
	if (!block) return null;

	const at = new Map(block.matrix.map((cell) => [`${cell.engine} ${cell.entity_id}`, cell.value]));
	const measured = block.matrix.map((cell) => cell.value).filter((n): n is number => n !== null);
	// Sentiment is signed, so its tint grows from the middle of the cell either
	// way; every other metric grows from the left, against the largest cell.
	const signed = metric === "sentiment";
	const max = Math.max(1, ...(signed ? measured.map(Math.abs) : measured));
	const tint = (cell: number): React.CSSProperties => {
		if (!signed) return { transform: `scaleX(${Math.max(0.02, cell / max)})` };
		// Half the cell is the whole scale on each side: scaled about the
		// centre, then shifted so the bar starts there.
		const share = Math.max(0.02, Math.abs(cell) / max);
		return {
			transformOrigin: "center",
			transform: `translateX(${cell < 0 ? -share * 25 : share * 25}%) scaleX(${share / 2})`,
		};
	};

	return (
		<div className="table-scroll">
			<Table variant="resource" className="grid-table">
				<TableHeader>
					<TableRow>
						<TableHead className="wrap">{t("insights.brand")}</TableHead>
						{data.platforms.map((engine) => (
							<TableHead key={engine} className="num">
								{t(`engine.${engine}`)}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{data.assets.map((asset) => (
						<TableRow key={asset.entity_id} className={asset.is_you ? "is-you" : undefined}>
							<TableCell className="wrap">{asset.name}</TableCell>
							{data.platforms.map((engine) => {
								const cell = at.get(`${engine} ${asset.entity_id}`) ?? null;
								return (
									<TableCell key={engine} className="cell-value">
										{cell === null ? null : <i style={tint(cell)} aria-hidden />}
										<span>{value(locale, metric, cell)}</span>
									</TableCell>
								);
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
